/**
 * Field Operations Scheduler — admin schedules investigators for case
 * field operations with hourly / flat-rate pay calculation, assignment
 * lifecycle, investigator accept/decline, check-in/out, and logs.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BACKEND_URL } from '../config/canonical';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  CalendarClock, Plus, Pencil, Trash2, MapPin, Clock, DollarSign,
  Loader2, RefreshCw, CheckCircle2, XCircle, LogIn, LogOut as LogOutIcon, ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';

const TOKEN_KEY = 'nexus_token';
const ADMIN_ROLES = ['main_hub', 'department_admin', 'super_admin'];

const apiCall = async (path, opts = {}) => {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

const STATUS_PALETTE = {
  SCHEDULED:  'bg-blue-500/20 border-blue-500/40 text-blue-200',
  ACCEPTED:   'bg-emerald-500/20 border-emerald-500/40 text-emerald-200',
  DECLINED:   'bg-red-500/20 border-red-500/40 text-red-200',
  CHECKED_IN: 'bg-amber-500/20 border-amber-500/40 text-amber-200',
  IN_PROGRESS:'bg-amber-500/20 border-amber-500/40 text-amber-200',
  COMPLETED:  'bg-slate-500/20 border-slate-500/40 text-slate-200',
  CANCELLED:  'bg-slate-500/20 border-slate-500/40 text-slate-200',
};
const PRIORITY_PALETTE = {
  LOW:    'bg-slate-500/20 border-slate-500/40 text-slate-200',
  NORMAL: 'bg-blue-500/20 border-blue-500/40 text-blue-200',
  HIGH:   'bg-amber-500/20 border-amber-500/40 text-amber-200',
  URGENT: 'bg-red-500/20 border-red-500/40 text-red-200',
};

const emptyForm = {
  case_id: '', case_number: '', case_name: '', client_name: '',
  operation_title: '', operation_type: 'SURVEILLANCE',
  assigned_investigator_ids: [], lead_investigator_id: '',
  date: '', start_time: '09:00', end_time: '17:00',
  estimated_hours: 8,
  operation_location: '', meeting_location: '',
  task_instructions: '', operational_notes: '', equipment_needed: '',
  priority: 'NORMAL',
  pay_type: 'HOURLY', hourly_rate: 45, flat_rate: 0,
};

export default function FieldOperations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [ops, setOps] = useState([]);
  const [meta, setMeta] = useState({ types: [], priorities: [] });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [opsRes, metaRes] = await Promise.all([
        apiCall(`/api/field-operations${statusFilter !== 'ALL' ? `?status=${statusFilter}` : ''}`).catch(() => ({ operations: [] })),
        apiCall('/api/field-operations/meta/options').catch(() => ({ types: [], priorities: [] })),
      ]);
      setOps(opsRes.operations || []);
      setMeta(metaRes);
    } catch (e) {
      toast.error(`Load failed: ${e.message}`);
    } finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => navigate('/field-operations/new');
  const openEdit = (op) => navigate(`/field-operations/${op.id}/edit`);

  const remove = async (op) => {
    if (!window.confirm(`Delete operation "${op.operation_title}"?`)) return;
    try {
      await apiCall(`/api/field-operations/${op.id}`, { method: 'DELETE' });
      toast.success('Deleted');
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  const respond = async (op, response) => {
    try {
      await apiCall(`/api/field-operations/${op.id}/respond`, { method: 'POST', body: JSON.stringify({ response }) });
      toast.success(response === 'ACCEPTED' ? 'Accepted' : 'Declined');
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  const checkAction = async (op, action) => {
    try {
      let lat = null, lng = null;
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 4000 }));
          lat = pos.coords.latitude; lng = pos.coords.longitude;
        } catch (e) { /* GPS optional */ }
      }
      await apiCall(`/api/field-operations/${op.id}/checkin`, { method: 'POST', body: JSON.stringify({ action, lat, lng }) });
      toast.success(action === 'CHECK_IN' ? 'Checked in' : 'Checked out');
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  const complete = async (op) => {
    try {
      await apiCall(`/api/field-operations/${op.id}/complete`, { method: 'POST' });
      toast.success('Marked completed');
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5 nx-form-with-action-bar" data-testid="field-ops-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <CalendarClock className="w-6 h-6 text-amber-300" /> Field Operations Schedule
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            {isAdmin ? 'Schedule investigators for case field work · Hourly or flat-rate pay'
                     : 'Your scheduled field operations'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => navigate('/field-operations/time')}
            className="bg-emerald-500 hover:bg-emerald-400 !text-slate-900 font-semibold"
            data-testid="fops-time-clock-btn"
          >
            <Clock className="w-4 h-4 mr-1.5" /> Investigator Time Clock
          </Button>
          {isAdmin && (
            <Button
              onClick={() => navigate('/field-operations/admin-reports')}
              variant="outline"
              className="border-slate-600 !text-slate-200"
              data-testid="fops-admin-reports-btn"
            >
              <ClipboardList className="w-4 h-4 mr-1.5" /> Admin Reports
            </Button>
          )}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 bg-slate-800 border-slate-600 text-slate-200" data-testid="fops-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All statuses</SelectItem>
              {(meta.statuses || []).map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={fetchAll} variant="outline" className="border-slate-600 !text-slate-200" data-testid="fops-refresh">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          {isAdmin && (
            <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" data-testid="fops-new">
              <Plus className="w-4 h-4 mr-1.5" /> New Operation
            </Button>
          )}
        </div>
      </div>

      {/* Time Clock Quick Access Card */}
      <Card className="bg-gradient-to-br from-emerald-900/40 to-slate-900 border-emerald-500/30" data-testid="fops-time-clock-card">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Clock className="w-6 h-6 text-emerald-300" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Investigator Time Clock</h3>
              <p className="text-xs text-slate-400 mt-0.5">Start / stop operation timers · Log time per case</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/field-operations/time')}
            className="bg-emerald-500 hover:bg-emerald-400 !text-slate-900 font-semibold"
            data-testid="fops-time-clock-open"
          >
            Open Time Clock →
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
      ) : ops.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-700 rounded">
          <CalendarClock className="w-12 h-12 mx-auto text-slate-600 mb-2" />
          <p className="text-white font-semibold">No operations scheduled</p>
          <p className="text-xs text-slate-400 mt-1">
            {isAdmin ? 'Click "New Operation" to schedule investigators for case field work.'
                     : 'You have no scheduled field operations.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="fops-list">
          {ops.map(op => (
            <Card key={op.id} className="bg-[#131A2A] border-slate-700" data-testid={`fops-row-${op.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-semibold text-white">{op.operation_title}</h3>
                    <p className="text-[11px] text-slate-400 font-mono">{op.id} · {op.operation_type?.replace(/_/g, ' ')}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge className={`${STATUS_PALETTE[op.status] || ''} border text-[10px] font-mono`}>{op.status}</Badge>
                    <Badge className={`${PRIORITY_PALETTE[op.priority] || ''} border text-[10px] font-mono`}>{op.priority}</Badge>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-300">
                  <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-slate-500" /> {op.date} · {op.start_time}{op.end_time ? `–${op.end_time}` : ''}</div>
                  <div className="flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5 text-emerald-400" />
                    {op.pay_type === 'FLAT' ? `$${op.flat_rate || 0} flat` : `${op.estimated_hours}h × $${op.hourly_rate}`} <span className="text-emerald-300 font-bold">= ${op.estimated_total_pay}</span>
                  </div>
                  {op.operation_location && (
                    <div className="col-span-2 flex items-center gap-1.5 truncate"><MapPin className="w-3.5 h-3.5 text-slate-500" /> {op.operation_location}</div>
                  )}
                  {(op.case_number || op.client_name) && (
                    <div className="col-span-2 text-[11px] text-slate-400">
                      {op.case_number && <>Case <span className="text-slate-200 font-mono">{op.case_number}</span></>}
                      {op.client_name && <> · {op.client_name}</>}
                    </div>
                  )}
                </div>
                {op.task_instructions && (
                  <p className="text-xs text-slate-400 mt-2 line-clamp-2">{op.task_instructions}</p>
                )}
                <div className="flex items-center gap-1.5 flex-wrap mt-3 pt-2 border-t border-slate-700/60">
                  {isAdmin ? (
                    <>
                      <Button onClick={() => openEdit(op)} size="sm" variant="outline" className="border-slate-500 !text-slate-200" data-testid={`fops-edit-${op.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                      <Button onClick={() => remove(op)} size="sm" variant="outline" className="border-red-500/40 !text-red-300 hover:bg-red-500/10" data-testid={`fops-delete-${op.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                      </Button>
                    </>
                  ) : (
                    <>
                      {!op.responses?.[user?.id] && (
                        <>
                          <Button onClick={() => respond(op, 'ACCEPTED')} size="sm" className="bg-emerald-500 hover:bg-emerald-400 !text-slate-900 font-semibold" data-testid={`fops-accept-${op.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Accept
                          </Button>
                          <Button onClick={() => respond(op, 'DECLINED')} size="sm" variant="outline" className="border-red-500/40 !text-red-300 hover:bg-red-500/10" data-testid={`fops-decline-${op.id}`}>
                            <XCircle className="w-3.5 h-3.5 mr-1" /> Decline
                          </Button>
                        </>
                      )}
                      {op.status === 'ACCEPTED' && (
                        <Button onClick={() => checkAction(op, 'CHECK_IN')} size="sm" className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" data-testid={`fops-checkin-${op.id}`}>
                          <LogIn className="w-3.5 h-3.5 mr-1" /> Check In
                        </Button>
                      )}
                      {(op.status === 'CHECKED_IN' || op.status === 'IN_PROGRESS') && (
                        <>
                          <Button onClick={() => checkAction(op, 'CHECK_OUT')} size="sm" variant="outline" className="border-slate-500 !text-slate-200" data-testid={`fops-checkout-${op.id}`}>
                            <LogOutIcon className="w-3.5 h-3.5 mr-1" /> Check Out
                          </Button>
                          <Button onClick={() => complete(op)} size="sm" className="bg-emerald-500 hover:bg-emerald-400 !text-slate-900 font-semibold" data-testid={`fops-complete-${op.id}`}>
                            <ClipboardList className="w-3.5 h-3.5 mr-1" /> Mark Completed
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

    </div>
  );
}
